import React from 'react'

import {
  ChartLineIcon,
  GaugeIcon,
  MapPinIcon,
  Table2Icon,
  type LucideIcon,
} from 'lucide-react'

import { Stack, Typography } from '@mui/material'

import { type AnalyticsCardCategory } from 'components/analytics/analyticsGrid'
import { ANALYTICS_CARD_CATEGORIES } from 'components/analytics/analyticsGrid'

/**
 * An analytics card's FORM — Metric / Chart / Map — as plain METADATA.
 *
 * ## This was a coloured badge, and the badge is gone
 *
 * Three passes narrowed it and each one removed something: a filled pill with a tinted
 * ground, then an outline with no fill, now no chrome at all — a glyph and a word. That is
 * a convergence, not a retreat. Every pass was answering the same complaint (the mark
 * outweighed the card TITLE, which is the thing you actually choose between) and the
 * chrome was what carried the weight, so removing it was always the terminal answer.
 *
 * ## NO COLOUR SURVIVES, and that is a decision rather than an omission
 *
 * The hues were the badge's whole justification: with 19 near-identical rows, colour was
 * what let you find the charts without reading. Two things removed the need.
 *
 * First, the label is now RIGHT-ALIGNED into a column — the words "Metric", "Chart" and
 * "Map" line up at a fixed x on every row, so the eye already has a single track to run
 * down and the word is doing the scanning colour was hired for. Second, and decisively:
 * with the pill gone, a coloured glyph would be the ONLY saturated thing in a row of
 * `text.secondary` metadata. It would out-weigh the title again at a smaller size, which
 * is the exact complaint this change exists to close — the fix would reintroduce the bug.
 *
 * So the whole element is `text.secondary`: it reads as metadata, which is what it is.
 * There is no contrast question left to validate (a 3:1 mark obligation only exists for a
 * mark carrying meaning by colour; nothing here does) — the label is text and wears a text
 * token at ~8.9:1 on the dark panel surface.
 *
 * Identity is still carried twice, by GLYPH SHAPE and by the WORD, so the element is fully
 * readable in greyscale — which it now literally is.
 *
 * Glyphs: a gauge for Metric (one reading on a dial), a line chart for Chart, a map pin for
 * Map. Each depicts the form it names, which is what lets the glyph carry identity beside
 * the word rather than merely decorating it.
 */
/**
 * ⚠️ COMPONENTS, not rendered elements, and that change is what keeps the map single.
 *
 * It held pre-sized `<GaugeIcon size={14}/>` nodes, which made 14px part of the map's
 * identity — so the first surface wanting the metric glyph at any other size had no
 * way to ask for it, and the `Settings -> Business figures` "Used by ... metric" block
 * did exactly what that leaves you to do: it imported a different chart icon
 * altogether. The map stayed "single" only because nobody could use it.
 *
 * Holding the component and taking size/weight at the call site means a second surface
 * consumes THIS table rather than reaching past it — which is the actual §36 property
 * wanted here. Size is a per-surface rendering parameter; the category→glyph MAPPING is
 * the thing that must not be duplicated, and it still is not.
 */
const CATEGORY_ICON: Record<AnalyticsCardCategory, LucideIcon> = {
  metric: GaugeIcon,
  chart: ChartLineIcon,
  map: MapPinIcon,
  /**
   * VERA-260. `Table2` — a grid with a header row — over `ListOrdered` (which reads as a
   * numbered list, i.e. the RANKING rather than the tabular form) and over `Rows3`
   * (rows without columns, which is the one thing that distinguishes a table from a
   * list). The glyph names the FORM, like its three siblings, not what the first card
   * built on it happens to rank.
   */
  table: Table2Icon,
}

/** The canonical size and weight, used unless a surface states otherwise. */
const GLYPH_SIZE = 14
const GLYPH_STROKE = 1.5

/**
 * The category's glyph alone, neutral — for the type filter's option rows.
 *
 * The menu has three rows, each with its word spelled out beside the mark, so a hue would
 * discriminate nothing the reader is not already told. `text.secondary`, so the glyph reads
 * as part of its label rather than as a second mark.
 *
 * Exported from HERE rather than rebuilt in the filter so the icon table has one home
 * (§36): a second `category -> glyph` map is how a category added later ends up with a
 * gauge in one place and nothing in the other.
 *
 * `size` and `strokeWidth` are open so a surface with its own icon scale consumes this
 * rather than importing a lucide glyph directly and re-deciding which one means
 * "metric" — see `CATEGORY_ICON`. They default to the canonical 14/1.5, so the existing
 * call sites are unchanged.
 */
export const AnalyticsCategoryGlyph = ({
  category,
  size = GLYPH_SIZE,
  strokeWidth = GLYPH_STROKE,
  color = 'text.secondary',
}: {
  category: AnalyticsCardCategory
  size?: number
  strokeWidth?: number
  /**
   * Open for the same reason `size` is: a surface that renders the glyph inside a
   * control with its OWN colour state needs it to follow that state, and the only
   * alternative is importing a lucide icon directly and re-deciding which one means
   * "chart" — the duplicate mapping this table exists to prevent (§36).
   *
   * `"inherit"` is the value that case wants. The default is unchanged, so every
   * existing call site renders identically.
   */
  color?: string
}) => {
  const Icon = CATEGORY_ICON[category]
  return (
    <Stack component="span" aria-hidden sx={{ color, display: 'inline-flex' }}>
      <Icon size={size} strokeWidth={strokeWidth} />
    </Stack>
  )
}

interface AnalyticsCategoryLabelProps {
  category: AnalyticsCardCategory
}

/**
 * Glyph + word, right-aligned into a column on the card library's rows.
 *
 * `flexShrink: 0` and `whiteSpace: nowrap` so it never wraps or compresses — it shares the
 * TITLE's row, and the title (which flexes and ellipsizes) is the element that should give
 * way when a row is narrow, not the two-word metadata beside it.
 */
export const AnalyticsCategoryLabel = ({ category }: AnalyticsCategoryLabelProps) => {
  const config = ANALYTICS_CARD_CATEGORIES[category]
  if (!config) {
    console.error(`Invalid analytics card 'category': ${category}`)
    return null
  }

  const Icon = CATEGORY_ICON[category]

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        flexShrink: 0,
        whiteSpace: 'nowrap',
        color: 'text.secondary',
        '& svg': { flexShrink: 0 },
      }}
    >
      <Icon size={GLYPH_SIZE} strokeWidth={GLYPH_STROKE} />
      {/* 12/500 — unchanged from the badge it replaces, so the type still sits on the same
          baseline beside the 14px title; only the chrome went. */}
      <Typography sx={{ fontWeight: 500, lineHeight: 1, fontSize: 12 }}>{config.label}</Typography>
    </Stack>
  )
}
